//@ts-check
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import * as authApi from '@/api/auth.api.js';
import { getAccessToken, setAccessToken, subscribeAccessToken } from '@/lib/authToken.js';

/**
 * @typedef {import('@/lib/permissions.js').AuthUser} AuthUser
 */

/**
 * @typedef {object} AuthContextValue
 * @property {AuthUser|null} user
 * @property {boolean} loading
 * @property {boolean} isAuthenticated
 * @property {(email: string, password: string) => Promise<AuthUser>} login
 * @property {() => Promise<void>} logout
 * @property {() => Promise<void>} refreshUser
 */

const AuthContext = createContext(/** @type {AuthContextValue|null} */ (null));

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(/** @type {AuthUser|null} */ (null));
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      return;
    }
    const { user: me } = await authApi.fetchMe();
    setUser(me);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const { accessToken, user: me } = await authApi.refreshSession();
        if (cancelled) return;
        setAccessToken(accessToken);
        setUser(me);
      } catch {
        if (!cancelled) {
          setAccessToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeAccessToken((token) => {
      if (!token) setUser(null);
    });
  }, []);

  const login = useCallback(
    /**
     * @param {string} email
     * @param {string} password
     */
    async (email, password) => {
      const { accessToken, user: me } = await authApi.login({ email, password });
      setAccessToken(accessToken);
      setUser(me);
      return me;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * @returns {AuthContextValue}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
